import { useCallback, useMemo, useState } from 'react';
import { useCreateRecordMutation } from '@platform/state/api';
import type { IApiErrorPlain, IRecord } from '@platform/core/types';
import { changedKeys, normalizeValue } from '../draftChanges';
import type { IRecordEditor } from '../IRecordEditor';
import type { IRecordEditorOptions } from '../IRecordEditorOptions';
import type { INewRecordEditor } from './INewRecordEditor';
import { newRecordValues } from './newRecordValues';

/**
 * Чернетка нового запису (прототип v4: новий запис відкривається в повній формі в режимі редагування; тут його ще не створено):
 * зміни — у чернетці поверх типових значень («Зберегти · N»); «Зберегти» перевіряє чернетку й створює запис (POST) — `created`;
 * «Скасувати» відкидає чернетку — `cancelled`. Дії, що змінюють збережений запис одразу (patchNow), пишуть у чернетку.
 */
export function useNewRecordEditor(ws: string, rt: string, record: IRecord, options: IRecordEditorOptions = {}): INewRecordEditor {
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [tags, setTags] = useState<string[] | null>(null);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<IRecord | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [create, { isLoading, error }] = useCreateRecordMutation();
  const dirtyKeys = useMemo(() => changedKeys(record, draft, tags), [record, draft, tags]);
  const setField = useCallback((key: string, value: unknown) => {
    setDraft((d) => ({ ...d, [key]: normalizeValue(value) }));
    setClientErrors((e) => { if (!(key in e)) return e; const { [key]: _gone, ...rest } = e; return rest; });
  }, []);
  const clear = useCallback(() => { setDraft({}); setTags(null); setClientErrors({}); }, []);
  const validate = options.validate;
  const save = useCallback(async () => {
    const invalid = validate?.(draft) ?? {};
    if (Object.keys(invalid).length) { setClientErrors(invalid); return false; }
    try {
      const rec = await create({ ws, rt, body: { values: newRecordValues(record, draft), tags: tags ?? record.tags } }).unwrap();
      clear();
      setCreated(rec);
      return true;
    } catch { return false; }
  }, [validate, draft, create, ws, rt, record, tags, clear]);
  const fieldErrors = useMemo(() => {
    const e = error as IApiErrorPlain | undefined;
    return { ...Object.fromEntries(Object.entries(e?.errors ?? {}).map(([k, v]) => [k, v.join('; ')])), ...clientErrors };
  }, [error, clientErrors]);
  const editor = useMemo<IRecordEditor>(() => ({
    draft, tags: tags ?? record.tags, editing: true, dirty: dirtyKeys.length > 0, dirtyKeys, saving: isLoading, error: error as IApiErrorPlain | undefined,
    fieldErrors, setField, setTags, save, reset: clear,
    patchNow: async (values: Record<string, unknown>) => { Object.entries(values).forEach(([k, v]) => setField(k, v)); return true; },
    startEdit: () => undefined,
    cancelEdit: () => { clear(); setCancelled(true); },
  }), [draft, tags, record.tags, dirtyKeys, isLoading, error, fieldErrors, setField, save, clear]);
  return { editor, created, cancelled };
}
